import { Link } from "react-router-dom";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import { getBaseUrl, buildBreadcrumbSchema } from "@/lib/seo";

const PRIVACY_TITLE = "Política de Privacidade | FabChat";
const PRIVACY_DESCRIPTION =
  "Política de Privacidade da FabChat. Como coletamos, usamos e protegemos seus dados pessoais na plataforma de agentes de IA para WhatsApp e Instagram.";

const breadcrumbItems = [
  { name: "Início", path: "/" },
  { name: "Política de Privacidade", path: "/privacidade" },
];

type PrivacyPolicyProps = {
  canonicalPath?: string;
};

export default function PrivacyPolicy({ canonicalPath = "/privacidade" }: PrivacyPolicyProps) {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, breadcrumbItems);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={PRIVACY_TITLE}
        description={PRIVACY_DESCRIPTION}
        canonicalPath={canonicalPath}
      />
      <StructuredData data={breadcrumbSchema} />
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center font-bold text-xl">
                        <img src="/logo-light.png?v=6" alt="FabChat Logo" className="h-10 w-auto object-contain block dark:hidden" />
                        <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 w-auto object-contain hidden dark:block" />
                    </Link>
                    <Link
                        to="/"
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Voltar ao início
                    </Link>
                </div>
            </header>

            <main className="container mx-auto px-4 py-12 max-w-4xl">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                    <h1 className="text-4xl font-bold mb-4">Política de Privacidade</h1>
                    <p className="text-muted-foreground mb-8">
                        <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </p>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">1. Introdução</h2>
                        <p className="mb-4">
                            A FabChat ("nós", "nosso" ou "empresa") está comprometida em proteger a privacidade e os dados pessoais de nossos usuários. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você utiliza nossa plataforma de automação de atendimento via WhatsApp e Instagram.
                        </p>
                        <p className="mb-4">
                            Ao utilizar os serviços da FabChat, você concorda com as práticas descritas nesta política. Se não concordar com esta política, por favor, não utilize nossos serviços.
                        </p>
                        <p className="mb-4">
                            Esta política deve ser lida em conjunto com nossos <Link to="/termos" className="text-primary hover:underline">Termos de Serviço</Link> e nossa <Link to="/cookies" className="text-primary hover:underline">Política de Cookies</Link>.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">2. Informações que Coletamos</h2>

                        <h3 className="text-xl font-semibold mb-3">2.1. Informações Fornecidas por Você</h3>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li><strong>Dados de Cadastro:</strong> Nome completo, endereço de e-mail, senha e informações de pagamento</li>
                            <li><strong>Dados do Negócio:</strong> Nome da empresa, CNPJ, endereço comercial e informações de contato</li>
                            <li><strong>Configurações do Agente:</strong> Informações sobre seus agentes de IA, regras de negócio e preferências de atendimento</li>
                            <li><strong>Dados de Integração:</strong> Credenciais e configurações de integração com WhatsApp, Instagram e outras plataformas</li>
                        </ul>

                        <h3 className="text-xl font-semibold mb-3">2.2. Informações Coletadas Automaticamente</h3>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li><strong>Dados de Uso:</strong> Informações sobre como você utiliza nossa plataforma, incluindo páginas visitadas, funcionalidades utilizadas e tempo de uso</li>
                            <li><strong>Dados Técnicos:</strong> Endereço IP, tipo de navegador, sistema operacional, identificadores de dispositivo e logs de acesso</li>
                            <li><strong>Cookies e Tecnologias Similares:</strong> Utilizamos cookies e tecnologias similares para melhorar sua experiência e analisar o uso da plataforma</li>
                        </ul>

                        <h3 className="text-xl font-semibold mb-3">2.3. Dados de Terceiros (Seus Clientes)</h3>
                        <p className="mb-4">
                            Quando você utiliza nossos serviços para automatizar atendimento, coletamos e processamos dados de seus clientes através das conversas realizadas via WhatsApp e Instagram, incluindo:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>Nomes, números de telefone e identificadores de usuário</li>
                            <li>Histórico de conversas e mensagens trocadas</li>
                            <li>Informações fornecidas voluntariamente pelos clientes durante as conversas</li>
                            <li>Metadados das conversas (horários, canais utilizados, etc.)</li>
                        </ul>
                        <p className="mb-4">
                            <strong>Importante:</strong> Você é responsável por garantir que possui base legal adequada (consentimento ou legítimo interesse) para processar os dados de seus clientes através de nossa plataforma, conforme exigido pela LGPD.
                        </p>

                        <h3 className="text-xl font-semibold mb-3">2.4. Dados Sensíveis</h3>
                        <p className="mb-4">
                            A FabChat <strong>não coleta intencionalmente</strong> dados sensíveis conforme definidos na LGPD (Art. 5º, II), tais como origem racial ou étnica, convicções religiosas, opiniões políticas, filiação sindical, dados genéticos, biométricos, de saúde ou de vida sexual. Caso em algum fluxo específico seja necessário tratar dados sensíveis, informaremos previamente a finalidade e a base legal (consentimento ou outra prevista em lei) e solicitaremos o consentimento quando exigido.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">3. Como Utilizamos suas Informações</h2>
                        <p className="mb-4">Utilizamos suas informações pessoais para os seguintes propósitos:</p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li><strong>Prestação de Serviços:</strong> Fornecer, manter e melhorar nossos serviços de automação de atendimento</li>
                            <li><strong>Processamento de Conversas:</strong> Processar e gerenciar conversas de seus clientes através dos canais integrados</li>
                            <li><strong>Comunicação:</strong> Enviar notificações sobre sua conta, atualizações de serviço e informações importantes</li>
                            <li><strong>Faturamento:</strong> Processar pagamentos, gerenciar assinaturas e emitir faturas</li>
                            <li><strong>Suporte Técnico:</strong> Prestar suporte e resolver problemas técnicos</li>
                            <li><strong>Melhorias:</strong> Analisar o uso da plataforma para identificar melhorias e desenvolver novos recursos</li>
                            <li><strong>Segurança:</strong> Detectar, prevenir e investigar atividades fraudulentas ou não autorizadas</li>
                            <li><strong>Conformidade Legal:</strong> Cumprir obrigações legais e regulatórias aplicáveis</li>
                        </ul>

                        <h3 className="text-xl font-semibold mb-3">3.1. Bases Legais para o Tratamento (LGPD Art. 7)</h3>
                        <p className="mb-4">
                            O tratamento dos seus dados pessoais é realizado com base nas seguintes bases legais previstas na LGPD (Lei 13.709/2018):
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li><strong>Execução de contrato (Art. 7º, V):</strong> Dados de cadastro, faturamento, configurações do agente e integrações são tratados para viabilizar a prestação dos serviços e o cumprimento do contrato.</li>
                            <li><strong>Cumprimento de obrigação legal (Art. 7º, II):</strong> Quando o tratamento for necessário para atender obrigações legais, regulatórias ou fiscais.</li>
                            <li><strong>Legítimo interesse (Art. 7º, IX):</strong> Segurança da plataforma, prevenção de fraude, detecção de uso indevido e melhorias técnicas são realizados com base em nosso legítimo interesse, respeitados seus direitos e liberdades.</li>
                            <li><strong>Consentimento (Art. 7º, I):</strong> Cookies não essenciais, comunicações de marketing (quando aplicável) e outras finalidades opcionais dependem do seu consentimento, que pode ser revogado a qualquer momento.</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">4. Compartilhamento de Informações</h2>
                        <p className="mb-4">Não vendemos suas informações pessoais. Podemos compartilhar suas informações apenas nas seguintes situações:</p>

                        <h3 className="text-xl font-semibold mb-3">4.1. Prestadores de Serviços</h3>
                        <p className="mb-4">
                            Compartilhamos informações com prestadores de serviços terceirizados que nos auxiliam na operação da plataforma, incluindo:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>Provedores de hospedagem e infraestrutura em nuvem (Supabase)</li>
                            <li>Processadores de pagamento (Stripe)</li>
                            <li>Provedores de serviços de IA e processamento de linguagem natural</li>
                            <li>Serviços de análise e monitoramento</li>
                        </ul>
                        <p className="mb-4">
                            Todos os prestadores de serviços são contratualmente obrigados a proteger suas informações e utilizá-las apenas para os fins especificados.
                        </p>

                        <h3 className="text-xl font-semibold mb-3">4.2. Integrações de Terceiros</h3>
                        <p className="mb-4">
                            Quando você integra nossa plataforma com serviços de terceiros (WhatsApp, Instagram, etc.), compartilhamos apenas as informações necessárias para o funcionamento da integração, conforme autorizado por você.
                        </p>

                        <h3 className="text-xl font-semibold mb-3">4.3. Requisitos Legais</h3>
                        <p className="mb-4">
                            Podemos divulgar informações se exigido por lei, ordem judicial ou processo legal, ou para proteger nossos direitos, propriedade ou segurança, bem como dos nossos usuários.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">5. Segurança dos Dados</h2>
                        <p className="mb-4">
                            Implementamos medidas de segurança técnicas e organizacionais adequadas para proteger suas informações pessoais contra acesso não autorizado, alteração, divulgação ou destruição, incluindo:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>Criptografia de dados em trânsito e em repouso</li>
                            <li>Autenticação de dois fatores e controles de acesso rigorosos</li>
                            <li>Monitoramento contínuo de segurança e detecção de ameaças</li>
                            <li>Backups regulares e planos de recuperação de desastres</li>
                            <li>Treinamento regular de equipe em segurança de dados</li>
                        </ul>
                        <p className="mb-4">
                            No entanto, nenhum método de transmissão pela internet ou armazenamento eletrônico é 100% seguro. Embora nos esforcemos para proteger suas informações, não podemos garantir segurança absoluta.
                        </p>

                        <h3 className="text-xl font-semibold mb-3">5.1. Comunicação de Incidentes de Segurança</h3>
                        <p className="mb-4">
                            Em caso de incidente de segurança que possa acarretar risco ou dano relevante aos titulares de dados, a FabChat comunicará os afetados e a Autoridade Nacional de Proteção de Dados (ANPD) nos termos da LGPD. Nos esforçaremos para notificar os titulares afetados em prazo razoável (em regra, em até 24 a 48 horas após tomarmos conhecimento do incidente), quando aplicável, informando a natureza dos dados envolvidos e as medidas adotadas.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">6. Seus Direitos sob a LGPD</h2>
                        <p className="mb-4">Conforme a Lei Geral de Proteção de Dados (LGPD), você possui os seguintes direitos:</p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li><strong>Confirmação e Acesso:</strong> Solicitar confirmação sobre o tratamento de seus dados e acessar seus dados pessoais</li>
                            <li><strong>Correção:</strong> Solicitar a correção de dados incompletos, inexatos ou desatualizados</li>
                            <li><strong>Anonimização, Bloqueio ou Eliminação:</strong> Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a LGPD</li>
                            <li><strong>Portabilidade:</strong> Solicitar a portabilidade de seus dados para outro fornecedor de serviço</li>
                            <li><strong>Eliminação:</strong> Solicitar a eliminação de dados pessoais tratados com seu consentimento</li>
                            <li><strong>Informação:</strong> Obter informações sobre entidades públicas e privadas com as quais compartilhamos dados</li>
                            <li><strong>Revogação de Consentimento:</strong> Revogar seu consentimento a qualquer momento</li>
                        </ul>
                        <p className="mb-4">
                            Para exercer seus direitos, entre em contato conosco através do e-mail: <strong>atendimento.fabtech@gmail.com</strong>
                        </p>

                        <h3 className="text-xl font-semibold mb-3">6.1. Decisões Automatizadas e Perfilamento</h3>
                        <p className="mb-4">
                            Nossa plataforma utiliza inteligência artificial e lógica automatizada para processar conversas e prestar os serviços (por exemplo, respostas de agentes de IA, detecção de padrões de uso e prevenção de fraude). Quando decisões automatizadas possam afetar você de forma significativa (por exemplo, restrições de acesso ou limites de uso decorrentes de detecção de uso indevido), você tem o direito de solicitar revisão por pessoa, nos termos da LGPD. Para exercer esse direito, entre em contato em <strong>atendimento.fabtech@gmail.com</strong>.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">7. Retenção de Dados</h2>
                        <p className="mb-4">
                            Mantemos suas informações pessoais apenas pelo tempo necessário para cumprir os propósitos descritos nesta política, a menos que um período de retenção mais longo seja exigido ou permitido por lei.
                        </p>
                        <p className="mb-4">
                            Quando você cancela sua conta, excluímos ou anonimizamos suas informações pessoais, exceto quando a retenção for necessária para:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li>Cumprir obrigações legais ou regulatórias</li>
                            <li>Resolver disputas e fazer cumprir nossos acordos</li>
                            <li>Manter registros de segurança e prevenção de fraudes</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">8. Cookies e Tecnologias de Rastreamento</h2>
                        <p className="mb-4">
                            Utilizamos cookies e tecnologias similares para melhorar sua experiência, analisar o uso da plataforma e personalizar conteúdo. Você pode controlar o uso de cookies através das configurações do seu navegador.
                        </p>
                        <p className="mb-4">
                            Tipos de cookies que utilizamos:
                        </p>
                        <ul className="list-disc pl-6 mb-4 space-y-2">
                            <li><strong>Cookies Essenciais:</strong> Necessários para o funcionamento básico da plataforma</li>
                            <li><strong>Cookies de Desempenho:</strong> Coletam informações sobre como você utiliza a plataforma</li>
                            <li><strong>Cookies de Funcionalidade:</strong> Permitem que a plataforma lembre suas preferências</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">9. Transferência Internacional de Dados</h2>
                        <p className="mb-4">
                            Nossos serviços podem utilizar infraestrutura localizada fora do Brasil. Ao utilizar nossos serviços, você consente com a transferência de suas informações para esses locais. Garantimos que todas as transferências sejam realizadas em conformidade com a LGPD e com medidas de segurança adequadas.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">10. Privacidade de Menores</h2>
                        <p className="mb-4">
                            Nossos serviços são destinados a empresas e profissionais. Não coletamos intencionalmente informações pessoais de menores de 18 anos. Se tomarmos conhecimento de que coletamos informações de um menor sem o consentimento adequado, tomaremos medidas para excluir essas informações.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">11. Alterações nesta Política</h2>
                        <p className="mb-4">
                            Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre alterações significativas publicando a nova política nesta página e atualizando a data de "Última atualização". Recomendamos que você revise esta política periodicamente.
                        </p>
                        <p className="mb-4">
                            Documentos relacionados: <Link to="/termos" className="text-primary hover:underline">Termos de Serviço</Link> e <Link to="/cookies" className="text-primary hover:underline">Política de Cookies</Link>.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4">12. Contato</h2>
                        <p className="mb-4">
                            Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Privacidade ou ao tratamento de seus dados pessoais, entre em contato conosco:
                        </p>
                        <div className="bg-muted p-4 rounded-lg mb-4">
                            <p className="mb-2"><strong>FabChat</strong></p>
                            <p className="mb-2">E-mail (proteção de dados): <strong>atendimento.fabtech@gmail.com</strong></p>
                            <p className="mb-2">Suporte: <strong>atendimento.fabtech@gmail.com</strong></p>
                        </div>
                        <p className="mb-4">
                            O canal oficial para exercer seus direitos como titular de dados (acesso, correção, eliminação, portabilidade, revogação de consentimento, etc.) e para assuntos de proteção de dados é <strong>atendimento.fabtech@gmail.com</strong>. Nosso Encarregado de Proteção de Dados pode ser contactado por esse canal.
                        </p>
                        <p className="mb-4">
                            Você também tem o direito de apresentar uma reclamação à Autoridade Nacional de Proteção de Dados (ANPD) se considerar que o tratamento de seus dados pessoais viola a LGPD.
                        </p>
                    </section>

                    <div className="mt-12 pt-8 border-t border-border">
                        <Link
                            to="/"
                            className="text-primary hover:underline"
                        >
                            ← Voltar ao início
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
}

